'use client';

import type { Placement } from '@floating-ui/react';
import DOMPurify from 'dompurify';
import parse, { DOMNode, domToReact } from 'html-react-parser';

import { FloatingTooltip } from '../FloatingTooltip';

const DEFAULT_TOOLTIP_TRIGGER_CLASS_NAME =
  'bg-custom-surface-contrast-low inline-block rounded-[6px] border border-white/10 px-[1px]';

interface ParseWithTooltipProps {
  /** HTML-строка, в которой элемент с id="dev_tooltip" станет триггером тултипа. */
  text: string;

  /** Контент, который будет показан внутри тултипа. */
  tooltipContent: React.ReactNode;

  /** Класс для элемента-триггера тултипа. По умолчанию используется стандартное выделение в тексте. */
  className?: string;

  /** Класс для всплывающего блока тултипа. */
  tooltipClassName?: string;

  /** Позиция тултипа относительно триггера. */
  position?: Placement;
}

/**
 * Парсит HTML-строку и заменяет вложенный элемент с `id="dev_tooltip"`
 * на интерактивный tooltip-trigger.
 *
 * Используется для переводов, где часть текста должна остаться внутри общей
 * строки, но получить тултип. HTML перед парсингом очищается через DOMPurify.
 */
export const ParseWithTooltip = ({
  text,
  tooltipContent,
  className = DEFAULT_TOOLTIP_TRIGGER_CLASS_NAME,
  tooltipClassName,
  position = 'bottom',
}: ParseWithTooltipProps) => {
  if (!text || typeof text !== 'string') {
    return null;
  }

  const sanitizedText =
    typeof window !== 'undefined' ? DOMPurify.sanitize(text) : text;

  return parse(sanitizedText, {
    replace: (domNode) => {
      if ('attribs' in domNode && domNode.attribs?.id === 'dev_tooltip') {
        return (
          <FloatingTooltip
            label={domToReact(domNode.children as DOMNode[])}
            tooltip={tooltipContent}
            placement={position}
            className={className}
            tooltipClassName={tooltipClassName}
          />
        );
      }
    },
  });
};
